import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";

import "./CustomEPG.css";
import fallbackLogo from "../../assets/logo_black_white.png";
import { debugLog } from "../../utils/debugLog";

const CustomEPG = ({
  channels,
  epgData,
  onChannelSelect,
  onProgramSelect,
  onToggleFavorite,
  onProgramLongPress,
  currentTime = new Date(),
}) => {
  debugLog("CustomEPG received:", {
    channelsCount: channels?.length,
    epgDataCount: epgData?.length,
    firstChannel: channels?.[0],
  });
  const [hoveredProgram, setHoveredProgram] = useState(null);
  const gridRef = useRef(null);
  const timelineRef = useRef(null);
  const currentTimeIndicatorRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const programLongPressTimerRef = useRef(null);
  const [pressedChannel, setPressedChannel] = useState(null);
  const [pressedProgram, setPressedProgram] = useState(null);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const programTouchStartPos = useRef({ x: 0, y: 0 });
  const isScrollingRef = useRef(false);
  const isProgramScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  // Constants for grid dimensions
  const CHANNEL_HEIGHT = 90;
  const CHANNEL_WIDTH = 90;
  const TIME_SLOT_WIDTH = 240; // Width per hour
  const HOURS_TO_DISPLAY = 6; // Show 6 hours of programming

  // Calculate time boundaries
  const startTime = useMemo(() => {
    const start = new Date(currentTime);

    start.setHours(start.getHours() - 1, 0, 0, 0);

    return start;
  }, [currentTime]);

  const endTime = useMemo(() => {
    const end = new Date(startTime);

    end.setHours(end.getHours() + HOURS_TO_DISPLAY);

    return end;
  }, [startTime]);

  // Generate time slots for the timeline
  const timeSlots = useMemo(() => {
    const slots = [];
    const current = new Date(startTime);

    while (current < endTime) {
      slots.push({
        time: new Date(current),
        label: current.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
      });
      current.setMinutes(current.getMinutes() + 30); // 30-minute intervals
    }

    return slots;
  }, [startTime, endTime]);

  // Process EPG data for efficient rendering
  const processedPrograms = useMemo(() => {
    const programs = new Map();
    const now = new Date(); // Always use fresh current time for live status

    channels.forEach((channel) => {
      const channelPrograms = epgData.filter(
        (p) => p.channelUuid === channel.uuid
      );
      const processedChannelPrograms = [];

      channelPrograms.forEach((program) => {
        const programStart = new Date(program.since);
        const programEnd = new Date(program.till);

        // Only include programs that overlap with our time window
        if (programEnd > startTime && programStart < endTime) {
          const displayStart =
            programStart < startTime ? startTime : programStart;
          const displayEnd = programEnd > endTime ? endTime : programEnd;

          const left =
            ((displayStart - startTime) / (1000 * 60 * 60)) * TIME_SLOT_WIDTH;
          const width =
            ((displayEnd - displayStart) / (1000 * 60 * 60)) * TIME_SLOT_WIDTH;

          const isLive = programStart <= now && programEnd > now;
          let progress = 0;

          if (isLive) {
            const elapsed = now - programStart;
            const duration = programEnd - programStart;

            progress = Math.min(100, Math.max(0, (elapsed / duration) * 100));
          }

          processedChannelPrograms.push({
            ...program,
            left,
            width,
            isLive,
            progress,
            channel,
          });
        }
      });

      // If no programs, add placeholder
      if (processedChannelPrograms.length === 0) {
        processedChannelPrograms.push({
          id: `${channel.uuid}-placeholder`,
          channelUuid: channel.uuid,
          title: channel.title || "No Programming Information",
          description: "",
          since: startTime.toISOString(),
          till: endTime.toISOString(),
          left: 0,
          width: HOURS_TO_DISPLAY * TIME_SLOT_WIDTH,
          isLive: false,
          channel,
        });
      }

      programs.set(channel.uuid, processedChannelPrograms);
    });

    return programs;
  }, [channels, epgData, startTime, endTime]);

  // Calculate current time indicator position
  const currentTimePosition = useMemo(() => {
    const elapsed = (currentTime - startTime) / (1000 * 60 * 60);

    return elapsed * TIME_SLOT_WIDTH;
  }, [currentTime, startTime]);

  // Handle synchronized scrolling
  const handleGridScroll = useCallback(
    (e) => {
      const { scrollLeft } = e.target;

      // Mark as scrolling
      isScrollingRef.current = true;

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Reset scrolling flag after scroll stops
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 100);

      // Use requestAnimationFrame for smooth scrolling
      requestAnimationFrame(() => {
        // Sync timeline horizontal scroll
        if (timelineRef.current) {
          timelineRef.current.scrollLeft = scrollLeft;
        }

        // Update current time indicator position
        if (currentTimeIndicatorRef.current) {
          currentTimeIndicatorRef.current.style.transform = `translateX(${currentTimePosition + 90 - scrollLeft}px)`;
        }
      });
    },
    [currentTimePosition]
  );

  // Scroll to current time
  const scrollToNow = useCallback(() => {
    if (gridRef.current) {
      const scrollPosition = Math.max(
        0,
        currentTimePosition - (window.innerWidth - CHANNEL_WIDTH) / 2
      );

      gridRef.current.scrollLeft = scrollPosition;
    }
  }, [currentTimePosition]);

  // Auto-scroll to current time on mount
  useEffect(() => {
    scrollToNow();
  }, [scrollToNow]);

  // Update current time indicator every minute
  useEffect(() => {
    const updateTimeIndicator = () => {
      // Update the current time indicator position
      if (currentTimeIndicatorRef.current && gridRef.current) {
        const now = new Date();
        const elapsed = (now - startTime) / (1000 * 60 * 60);
        const newPosition = elapsed * TIME_SLOT_WIDTH;

        // Update the indicator position accounting for scroll
        currentTimeIndicatorRef.current.style.left = `${newPosition + 90}px`;
        currentTimeIndicatorRef.current.style.transform = `translateX(${-gridRef.current.scrollLeft}px)`;
      }
    };

    // Update immediately
    updateTimeIndicator();

    // Then update every 30 seconds for better accuracy
    const interval = setInterval(updateTimeIndicator, 30000);

    return () => clearInterval(interval);
  }, [startTime, TIME_SLOT_WIDTH]);

  const handleChannelClick = (channel) => {
    // Only trigger click if not a long press
    if (!longPressTimerRef.current) {
      if (onChannelSelect) {
        onChannelSelect(channel);
      }
    }
  };

  const handleChannelMouseDown = (channel, e) => {
    // Don't prevent default on touch events to allow scrolling on iOS
    if (!e.touches) {
      e.preventDefault();
    }
    setPressedChannel(channel.uuid);

    // Track touch/mouse start position
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    touchStartPos.current = { x: clientX, y: clientY };
    isScrollingRef.current = false;

    // Clear any existing timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    // Start long press timer (500ms)
    longPressTimerRef.current = setTimeout(() => {
      // Long press detected - toggle favorite
      if (onToggleFavorite && !isScrollingRef.current) {
        onToggleFavorite(channel);
      }
      longPressTimerRef.current = null;
      setPressedChannel(null);
    }, 500);
  };

  const handleChannelMouseUp = (channel, e) => {
    // If already marked as scrolling, don't do anything
    if (isScrollingRef.current) {
      setPressedChannel(null);
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      return;
    }

    // Calculate movement distance
    const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const clientY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
    const deltaX = Math.abs(clientX - touchStartPos.current.x);
    const deltaY = Math.abs(clientY - touchStartPos.current.y);
    const movementThreshold = 3; // Very low threshold for quick swipe detection

    // Determine if this was a scroll or a tap (prioritize vertical)
    const wasScrolling =
      deltaY > movementThreshold || deltaX > movementThreshold;

    // Clear long press timer if released early
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      // Only trigger click if not scrolling
      if (!wasScrolling) {
        handleChannelClick(channel);
      }
    } else if (!wasScrolling) {
      // Handle tap when timer already completed (but only if not scrolling)
      handleChannelClick(channel);
    }
    setPressedChannel(null);
  };

  const handleChannelMouseLeave = () => {
    // Clear timer if mouse leaves
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setPressedChannel(null);
  };

  const handleChannelTouchMove = (e) => {
    // Detect if user is scrolling
    const { clientX } = e.touches[0];
    const { clientY } = e.touches[0];
    const deltaX = Math.abs(clientX - touchStartPos.current.x);
    const deltaY = Math.abs(clientY - touchStartPos.current.y);
    const movementThreshold = 3; // Very low threshold for immediate detection

    // Prioritize vertical scrolling detection
    if (deltaY > movementThreshold || deltaX > movementThreshold) {
      isScrollingRef.current = true;
      setPressedChannel(null); // Clear pressed state immediately
      // Cancel long press if scrolling
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }
  };

  const handleProgramClick = (program, channel) => {
    // Only trigger click if not a long press
    if (!longPressTimerRef.current) {
      if (onProgramSelect) {
        onProgramSelect({ ...program, channel });
      }
    }
  };

  const handleProgramMouseDown = (program, channel, e) => {
    // Don't prevent default on touch events to allow scrolling
    if (!e.touches) {
      e.preventDefault();
    }
    e.stopPropagation();

    setPressedProgram(program.id);

    // Track touch/mouse start position
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    programTouchStartPos.current = { x: clientX, y: clientY };
    isProgramScrollingRef.current = false;

    // Clear any existing timer
    if (programLongPressTimerRef.current) {
      clearTimeout(programLongPressTimerRef.current);
    }

    // Check if this is a future program (not live or past)
    const programStart = new Date(program.since);
    const now = new Date();
    const isFutureProgram = programStart > now;

    // Start long press timer (500ms)
    programLongPressTimerRef.current = setTimeout(() => {
      if (isProgramScrollingRef.current) {
        programLongPressTimerRef.current = null;
        setPressedProgram(null);

        return;
      }

      // Long press detected
      if (isFutureProgram && onProgramLongPress) {
        // Future program - show reminder options
        onProgramLongPress(program, channel);
      } else if (onToggleFavorite) {
        // Live/past program - toggle favorite
        onToggleFavorite(channel);
      }
      programLongPressTimerRef.current = null;
      setPressedProgram(null);
    }, 500);
  };

  const handleProgramMouseUp = (program, channel, e) => {
    // If already marked as scrolling, don't do anything
    if (isProgramScrollingRef.current) {
      setPressedProgram(null);
      if (programLongPressTimerRef.current) {
        clearTimeout(programLongPressTimerRef.current);
        programLongPressTimerRef.current = null;
      }

      return;
    }

    // Calculate movement distance
    const clientX = e?.changedTouches
      ? e.changedTouches[0].clientX
      : e?.clientX || 0;
    const clientY = e?.changedTouches
      ? e.changedTouches[0].clientY
      : e?.clientY || 0;
    const deltaX = Math.abs(clientX - programTouchStartPos.current.x);
    const deltaY = Math.abs(clientY - programTouchStartPos.current.y);
    const movementThreshold = 5;

    const wasScrolling =
      deltaY > movementThreshold || deltaX > movementThreshold;

    // Clear long press timer if released early
    if (programLongPressTimerRef.current) {
      clearTimeout(programLongPressTimerRef.current);
      programLongPressTimerRef.current = null;
      // Only trigger click if not scrolling
      if (!wasScrolling) {
        handleProgramClick(program, channel);
      }
    }
    setPressedProgram(null);
  };

  const handleProgramMouseLeave = () => {
    // Clear timer if mouse leaves
    if (programLongPressTimerRef.current) {
      clearTimeout(programLongPressTimerRef.current);
      programLongPressTimerRef.current = null;
    }
    setPressedProgram(null);
  };

  const handleProgramTouchMove = (e) => {
    // Detect if user is scrolling
    const { clientX, clientY } = e.touches[0];
    const deltaX = Math.abs(clientX - programTouchStartPos.current.x);
    const deltaY = Math.abs(clientY - programTouchStartPos.current.y);
    const movementThreshold = 5;

    if (deltaY > movementThreshold || deltaX > movementThreshold) {
      isProgramScrollingRef.current = true;
      setPressedProgram(null);
      // Cancel long press if scrolling
      if (programLongPressTimerRef.current) {
        clearTimeout(programLongPressTimerRef.current);
        programLongPressTimerRef.current = null;
      }
    }
  };

  return (
    <div className="custom-epg-container">
      {/* Header Controls */}
      <div className="epg-header">
        <div className="epg-header-left">
          <h2 className="epg-title">Electronic Program Guide</h2>
        </div>
        <div className="epg-header-controls">
          <button
            className="epg-control-btn"
            onClick={() => {
              if (gridRef.current) {
                gridRef.current.scrollLeft -= TIME_SLOT_WIDTH;
              }
            }}
          >
            <i className="fas fa-chevron-left" />
          </button>
          <button
            className="epg-control-btn epg-control-btn-primary"
            onClick={scrollToNow}
          >
            NOW
          </button>
          <button
            className="epg-control-btn"
            onClick={() => {
              if (gridRef.current) {
                gridRef.current.scrollLeft += TIME_SLOT_WIDTH;
              }
            }}
          >
            <i className="fas fa-chevron-right" />
          </button>
        </div>
      </div>

      <div className="epg-layout">
        {/* Timeline */}
        <div className="epg-timeline" ref={timelineRef}>
          <div className="epg-timeline-inner">
            {timeSlots.map((slot, index) => (
              <div
                className="epg-timeline-slot"
                key={index}
                style={{ left: index * (TIME_SLOT_WIDTH / 2) }}
              >
                <span className="epg-timeline-label">{slot.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Programs grid with integrated logos */}
        <div className="epg-grid" ref={gridRef} onScroll={handleGridScroll}>
          {/* Current time indicator */}
          <div
            className="epg-current-time"
            ref={currentTimeIndicatorRef}
            style={{ left: currentTimePosition + 90 }}
          />

          {/* Programs */}
          <div className="epg-programs">
            {channels.map((channel) => (
              <div
                className="epg-channel-row"
                key={channel.uuid}
                style={{ height: CHANNEL_HEIGHT }}
              >
                {/* Channel Logo Cell */}
                <div
                  aria-label={`Play ${channel.title}`}
                  className={`epg-channel-logo-cell ${pressedChannel === channel.uuid ? "pressed" : ""}`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleChannelClick(channel);
                    }
                  }}
                  onMouseDown={(e) => handleChannelMouseDown(channel, e)}
                  onMouseLeave={handleChannelMouseLeave}
                  onMouseUp={(e) => handleChannelMouseUp(channel, e)}
                  onTouchEnd={(e) => handleChannelMouseUp(channel, e)}
                  onTouchMove={handleChannelTouchMove}
                  onTouchStart={(e) => handleChannelMouseDown(channel, e)}
                >
                  <img
                    alt={channel.title}
                    className="epg-channel-logo"
                    src={channel.logo || fallbackLogo}
                    onError={(e) => {
                      e.target.src = fallbackLogo;
                    }}
                  />
                  {channel.isBookmarked && (
                    <div className="epg-channel-favorite-star">
                      <i className="fas fa-star" />
                    </div>
                  )}
                </div>

                {/* Channel Programs */}
                <div className="epg-channel-programs">
                  {processedPrograms.get(channel.uuid)?.map((program) => (
                    <div
                      aria-label={`Select ${program.title}`}
                      className={`epg-program ${program.isLive ? "live" : ""} ${
                        hoveredProgram?.id === program.id ? "hovered" : ""
                      } ${pressedProgram === program.id ? "pressed" : ""}`}
                      key={program.id}
                      role="button"
                      style={{
                        left: program.left,
                        width: program.width,
                      }}
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleProgramClick(program, channel);
                        }
                      }}
                      onMouseDown={(e) =>
                        handleProgramMouseDown(program, channel, e)
                      }
                      onMouseEnter={() => setHoveredProgram(program)}
                      onMouseLeave={() => {
                        handleProgramMouseLeave();
                        setHoveredProgram(null);
                      }}
                      onMouseUp={(e) =>
                        handleProgramMouseUp(program, channel, e)
                      }
                      onTouchEnd={(e) =>
                        handleProgramMouseUp(program, channel, e)
                      }
                      onTouchMove={handleProgramTouchMove}
                      onTouchStart={(e) =>
                        handleProgramMouseDown(program, channel, e)
                      }
                    >
                      <div className="epg-program-content">
                        {program.image && (
                          <div
                            className="epg-program-thumbnail"
                            style={{ backgroundImage: `url(${program.image})` }}
                          />
                        )}
                        <div className="epg-program-info">
                          <div className="epg-program-title">
                            {program.title}
                          </div>
                          <div className="epg-program-time">
                            {new Date(program.since).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })}
                            {" - "}
                            {new Date(program.till).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })}
                          </div>
                          {program.isLive && (
                            <div className="epg-program-progress">
                              <div
                                className="epg-program-progress-bar"
                                style={{ width: `${program.progress}%` }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hover tooltip */}
      {hoveredProgram && (
        <div className="epg-tooltip">
          <div
            className="epg-tooltip-backdrop"
            style={{
              backgroundImage: `url(${hoveredProgram.image || hoveredProgram.channel?.logo || "/background.png"})`,
            }}
          >
            <div className="epg-tooltip-overlay" />
          </div>
          <div className="epg-tooltip-content">
            <h3 className="epg-tooltip-title">{hoveredProgram.title}</h3>
            <div className="epg-tooltip-channel">
              {hoveredProgram.channel?.logo && (
                <img
                  alt={hoveredProgram.channel.title}
                  className="epg-tooltip-channel-logo"
                  src={hoveredProgram.channel.logo}
                />
              )}
              <span>{hoveredProgram.channel?.title}</span>
            </div>
            <div className="epg-tooltip-time">
              {new Date(hoveredProgram.since).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
              {" - "}
              {new Date(hoveredProgram.till).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </div>
            {hoveredProgram.description && (
              <p className="epg-tooltip-description">
                {hoveredProgram.description}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomEPG;
